import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { TourData } from "../../../Types";
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { useGetQuiz, useGetTourQuizes } from "../../../api/calls/Quiz";
import SceneDelConfirmPanel from "../SceneDelConfirmPanel";
import { getLanguageFile } from "../../common/GetLanguageFile";
import { useEditingElementStore } from "../../../api/Zustand";
import { formatSeconds } from "../../TourViewer/escape-room/zustand";

function QuizSelector(props: {
  tourID: number;
  tourData: TourData;
  formData: TourData;
  setValue: UseFormSetValue<TourData>;
  register: UseFormRegister<TourData>;
  watch: UseFormWatch<TourData>;
}) {
  const { tourID, tourData, formData, setValue, register } = props;
  const [isChangingQuizData, setIsChanginQuizData] = React.useState<{
    open: boolean;
    quizId?: number;
  }>({ open: false });
  const [isRemovingQuizData, setIsRemovingQuizData] = React.useState(false);

  const queryClient = useQueryClient();

  const { quizData: tourQuizData, escapeRoomData } = tourData;

  const {
    error: quizListError,
    data: quizListData,
    isLoading,
  } = useGetTourQuizes(tourID);
  const {
    error: quizError,
    isError,
    data: quizData,
    isLoading: loadingQuizData,
  } = useGetQuiz(tourID, tourData.assignedQuizID);

  const removeQuizData = React.useCallback(() => {
    setValue("assignedQuizID", undefined, {
      shouldDirty: true,
    });
    setValue("quizData", undefined, { shouldDirty: true });

    queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      assignedQuizID: undefined,
      quizData: undefined,
      scenes: tourData.scenes.map((sc) => {
        return {
          ...sc,
          hotspots: sc.hotspots.filter((ones) => ones.type !== "quiz"),
        };
      }),
    });

    return;
  }, [setValue, tourID, queryClient, tourData]);

  const changeQuizData = React.useCallback(
    (id: number) => {
      return queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,

        assignedQuizID: id,
        scenes: tourData.scenes.map((sc) => {
          return {
            ...sc,
            hotspots: sc.hotspots.filter((ones) => ones.type !== "quiz"),
          };
        }),
      });
    },
    [tourID, queryClient, tourData]
  );

  const changingQuizWarning: boolean = React.useMemo(() => {
    if (
      !!tourData.scenes.find((sc) =>
        sc.hotspots.find((one) => one.type === "quiz")
      )
    )
      return true;
    else return false;
  }, [tourData.scenes]);

  return (
    <div>
      {isRemovingQuizData && (
        <SceneDelConfirmPanel
          handleSceneDelete={() => {
            removeQuizData();
            return;
          }}
          setIsDeletingScene={() => setIsRemovingQuizData(false)}
        >
          <div style={{ marginBottom: 10 }}>
            {getLanguageFile("right_panel.quiz_del_confirm")}
          </div>
        </SceneDelConfirmPanel>
      )}

      {isChangingQuizData.open && (
        <SceneDelConfirmPanel
          handleSceneDelete={() => {
            changeQuizData(isChangingQuizData.quizId!);
            return;
          }}
          differentConfirmBtnMessage={getLanguageFile("generics.confirm")}
          setIsDeletingScene={() =>
            setIsChanginQuizData({ open: false, quizId: undefined })
          }
        >
          <div style={{ marginBottom: 10 }}>
            {getLanguageFile("right_panel.quiz_change_confirm")}
          </div>
        </SceneDelConfirmPanel>
      )}
      {quizListError?.response?.status === 500 && (
        <code>{getLanguageFile("right_panel.no_available_quiz")}</code>
      )}
      {quizListError ? null : (
        <>
          {isLoading && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
              }}
            >
              <div
                id="spinner"
                style={{ width: 30, height: 30, justifySelf: "center" }}
              />
            </div>
          )}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: 30,
              }}
            >
              {tourData.assignedQuizID && (
                <button
                  type="button"
                  id="small"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    fontWeight: 900,
                    minWidth: 30,
                    height: 30,
                    borderRadius: "50%",
                  }}
                  onClick={() => {
                    if (changingQuizWarning) setIsRemovingQuizData(true);
                    else removeQuizData();
                  }}
                >
                  ✖
                </button>
              )}

              {quizListData?.data && (
                <select
                  id="groupSelector"
                  style={{ padding: 4 }}
                  {...register("assignedQuizID", {
                    valueAsNumber: true,
                    onChange: (e) => {
                      if (changingQuizWarning) {
                        setIsChanginQuizData({
                          open: true,
                          quizId: e.target.value,
                        });
                      } else changeQuizData(e.target.value);
                    },
                  })}
                  value={tourData.assignedQuizID ?? "default"}
                >
                  {!tourData.assignedQuizID || !formData.assignedQuizID ? (
                    <option
                      disabled
                      value="default"
                      style={{
                        color: "#282828aa",
                      }}
                    >
                      {getLanguageFile("right_panel.quiz_assign")}
                    </option>
                  ) : null}
                  {quizListData.data.map((quiz: any) => (
                    <option key={quiz.quizId} value={quiz.quizId}>
                      {quiz.quizTitle}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {tourQuizData && escapeRoomData && (
              <EscapeRoomDetails
                tourData={tourData}
                tourID={tourID}
                setValue={setValue}
                register={register}
                watch={props.watch}
              />
            )}
            {loadingQuizData && getLanguageFile("generics.loading")}
            {isError && getLanguageFile("right_panel.empty_quiz")}{" "}
          </div>
        </>
      )}
    </div>
  );
}

export default QuizSelector;

function EscapeRoomDetails(props: {
  tourData: TourData;
  tourID: number;
  setValue: UseFormSetValue<TourData>;
  register: UseFormRegister<TourData>;
  watch: UseFormWatch<TourData>;
}) {
  const { escapeRoomData } = props.tourData;

  const queryClient = useQueryClient();

  const { setEditingElem } = useEditingElementStore();

  const generatePopups = React.useCallback(() => {
    const startID = Math.floor(Math.random() * 10000000000);
    const endID = Math.floor(Math.random() * 10000000000);

    return queryClient.setQueryData(
      ["GetTour", props.tourID],
      (oldData: TourData) =>
        oldData
          ? {
              ...oldData,

              popups: [
                ...oldData.popups,
                { id: startID, text: "prova inizio", type: "text" },
                { id: endID, text: "prova fine", type: "text" },
              ],
              escapeRoomData: {
                ...oldData.escapeRoomData,
                startPopupID: startID,
                endPopupID: endID,
              },
            }
          : oldData
    );
  }, [queryClient, props.tourID]);

  const updatedEscapeRoomData = props.watch("escapeRoomData");

  return (
    <div
      style={{
        marginBottom: "1rem",
      }}
    >
      <div className="input-range-inline-wrapper">
        <h4>{getLanguageFile("right_panel.er_type_selector")}</h4>
        <select
          id="groupSelector"
          {...props.register("escapeRoomData.escapeRoomType", {
            valueAsNumber: true,
            setValueAs(value: string) {
              return parseInt(value);
            },
          })}
        >
          <option value={1}>{getLanguageFile("right_panel.er_timed")}</option>
          <option value={2}> {getLanguageFile("right_panel.er_points")}</option>
          <option value={3}> {getLanguageFile("right_panel.er_lives")}</option>
        </select>
      </div>
      {updatedEscapeRoomData?.escapeRoomType === 1 ? (
        <div className="input-range-inline-wrapper">
          <h4>{getLanguageFile("right_panel.er_duration_input")}</h4>
          <div className="input-range-inline">
            <input
              type="range"
              {...props.register("escapeRoomData.escapeRoomTime")}
              min={60}
              max={1200}
            ></input>

            <p>{formatSeconds(updatedEscapeRoomData?.escapeRoomTime || 0)}</p>
          </div>
        </div>
      ) : updatedEscapeRoomData?.escapeRoomType === 2 ? (
        <div></div>
      ) : updatedEscapeRoomData?.escapeRoomType === 3 ? (
        <div>
          {getLanguageFile("right_panel.er_max_lives")}
          <input
            {...props.register("escapeRoomData.escapeRoomLives")}
            type="number"
            max={15}
            min={3}
          ></input>
        </div>
      ) : null}
      <br />

      {!escapeRoomData?.startPopupID && (
        <button type="button" onClick={() => generatePopups()}>
          Genera popup fine e inizio
        </button>
      )}
      {escapeRoomData?.startPopupID && (
        <button
          type="button"
          onClick={() =>
            setEditingElem("escapeRoomPops", escapeRoomData.startPopupID!)
          }
        >
          Edit start
        </button>
      )}
      {escapeRoomData?.endPopupID && (
        <button
          type="button"
          onClick={() =>
            setEditingElem("escapeRoomPops", escapeRoomData.endPopupID!)
          }
        >
          Edit end
        </button>
      )}
      <></>
    </div>
  );
}

//  <select id="countries" class="bg-gray-50 border border-gray-300
//  text-gray-900 text-sm rounded-lg focus:ring-blue-500
//  focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700
//  dark:border-gray-600 dark:placeholder-gray-400 dark:text-white
//  dark:focus:ring-blue-500 dark:focus:border-blue-500">
